import { useState } from "react";
import {
  Form,
  FormSection,
  FeedbackDialog,
  AlertDialog,
  FormRow,
  ReadOnlyValue,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { taskEither } from "fp-ts";
import { pipe } from "fp-ts/function";
import { useFormatMessage } from "../../intl";
import { useCommand, useQuery } from "../../useAPI";
import * as apis from "./api";
import * as remoteData from "../../RemoteData";
import { WarningLargeIcon } from "@g4p/react-styleguide-library";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";

type Props = {
  onCancel: () => unknown;
  onConfirm: () => unknown;
};

export function SendContractsViaEmailDialog(props: Props) {
  const formatMessage = useFormatMessage();
  const isMobileLayout = useIsMobileLayout();
  const sendDocumentsCommand = useCommand(apis.sendDocumentsCommand);
  const [status, setStatus] = useState<"base" | "success" | "error">("base");

  const [emailQuery] = useQuery(apis.userEmail);

  const userEmail = pipe(
    emailQuery,
    remoteData.fold(
      () => "",
      () => "",
      ({ email }) => email
    )
  );

  const submitHandler = pipe(
    sendDocumentsCommand(),
    taskEither.bimap(
      () => setStatus("error"),
      () => setStatus("success")
    )
  );

  switch (status) {
    case "base":
      return (
        <AlertDialog
          ariaLabelMessageForCloseIcon={formatMessage("Modal.closeButton")}
          mobileModalHeight={"97dvh"}
          type="confirmation"
          title={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.title"
          )}
          message={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.description"
          )}
          onCancel={props.onCancel}
          onDismiss={props.onCancel}
          onConfirm={submitHandler}
          cancelLabel={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.cancel"
          )}
          confirmLabel={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.submit"
          )}
          icon={() => (
            <WarningLargeIcon
              width={"56px"}
              height={"56px"}
              color={palette.warning800}
            />
          )}
        >
          <Form>
            <FormSection>
              <FormRow type="full">
                <ReadOnlyValue
                  center={!isMobileLayout}
                  value={userEmail}
                  label={formatMessage(
                    "SummaryAndDownload.Summary.contracts.email.verifiedEmailLabel"
                  )}
                />
              </FormRow>
            </FormSection>
          </Form>
        </AlertDialog>
      );
    case "error":
      return (
        <FeedbackDialog
          ariaLabelMessageForCloseIcon={formatMessage("Modal.closeButton")}
          title={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.error.title"
          )}
          type="negative"
          cta={{
            label: formatMessage("Ok"),
            action: props.onConfirm,
          }}
        />
      );
    case "success":
      return (
        <FeedbackDialog
          ariaLabelMessageForCloseIcon={formatMessage("Modal.closeButton")}
          type="success"
          title={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.confirmation.title"
          )}
          subtitle={formatMessage(
            "SummaryAndDownload.Summary.contracts.email.confirmation.body"
          )}
          cta={{
            label: formatMessage(
              "SummaryAndDownload.Summary.contracts.email.confirmation.button"
            ),
            action: props.onConfirm,
          }}
        />
      );
  }
}
